import { isProjectSelectable } from '@wiztime/core';
import { useEffect, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutMode, useLayoutMode } from '../../../hooks/useMediaQuery';
import { cx } from '../../../lib/cx';
import { projectHex } from '../../../lib/projectColor';
import { Button, ButtonVariant } from '../../../ui/Button';
import { useDayData } from '../DayDataContext';
import { useDayUi } from '../dayUiStore';
import { quickLockHintActive, useGestureCounts } from '../gestureCount';
import { QuickMarkMode } from '../quickMark';
import { LockIcon } from './QuickMarkTile';
import styles from './Cards.module.css';

/**
 * Reminder above the timeline while quick mark is on (2026-10b §4.4), at every width: Once on
 * the soft accent bar, Locked on the stronger one with the "连续" pill. Also the single guard
 * that exits the mode when its project is deactivated or deleted (§4.1).
 */
export function QuickMarkBanner() {
  const { t } = useTranslation();
  const { projects } = useDayData();
  const quickMark = useDayUi((state) => state.quickMark);
  const counts = useGestureCounts((state) => state.counts);
  const narrow = useLayoutMode() === LayoutMode.Narrow;
  const project = quickMark.projectId === null ? undefined : projects.byId.get(quickMark.projectId);
  const gone = project === undefined || !isProjectSelectable(project, projects.byId);

  // The mode's project was deactivated or deleted: back to Off (§4.1 "模式的项目被停用或删除时退出").
  useEffect(() => {
    if (quickMark.mode !== QuickMarkMode.Off && gone && !projects.loading) {
      useDayUi.getState().quickMarkExit();
    }
  }, [quickMark.mode, gone, projects.loading]);

  if (quickMark.mode === QuickMarkMode.Off || project === undefined || gone) return null;
  const locking = quickMark.mode === QuickMarkMode.Locked;
  return (
    <div
      className={cx(styles.quickBanner, locking && styles.quickBannerLocked)}
      role="status"
      data-testid="quick-mark-banner"
      data-mode={quickMark.mode}
    >
      <span className={styles.quickBannerText}>
        <span
          className={styles.quickBannerDot}
          style={{ '--tile-color': projectHex(project, projects.byId) } as CSSProperties}
          aria-hidden="true"
        />
        {locking ? (
          <>
            {t(narrow ? 'quickMark.activeLockedShort' : 'quickMark.active', { project: project.data.name })}
            <span className={styles.quickBannerPill}>
              <LockIcon />
              {t('quickMark.locked')}
            </span>
            {narrow ? null : <span className={styles.quickBannerHint}>{t('quickMark.lockedHint')}</span>}
          </>
        ) : (
          <>
            {t('quickMark.activeOnce', { project: project.data.name })}
            {narrow ? null : (
              <span className={styles.quickBannerHint}>
                {t('quickMark.onceHint')}
                {quickLockHintActive(counts) ? ` · ${t('quickMark.lockTeach')}` : ''}
              </span>
            )}
          </>
        )}
      </span>
      <Button
        variant={ButtonVariant.Ghost}
        className={styles.quickBannerExit}
        aria-label={t('quickMark.exit')}
        onClick={() => useDayUi.getState().quickMarkExit()}
      >
        {t('quickMark.exitShort')}
      </Button>
    </div>
  );
}
